import { ChevronRightSharp } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  TableContainer,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import type { ProjectRunAnalysisQuestion, RubricCore } from '../../../../Types/dealGPT';
import { FeedbackInterface } from '../../../../Types/feedback';
import UnprocessedChangesDot from '../UnprocessedChangesDot';
import RubricOverallRating from './RubricOverallRating';
import RubricRow from './RubricRow';
import { calculateDimensionValues, calculateOverallRating } from './rubricUtils';

const Section = styled('div')({
  '&:not(:first-of-type)': {
    borderTop: '2px solid #C4C4C4',
    paddingTop: '40px',
  },
});

const RubricAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  paddingLeft: 0,
  paddingBottom: '20px',

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },

  '& .MuiAccordionSummary-content': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: '24px',
  },
});

const separator = '$$$-$$$';
interface RubricSectionProps {
  projectId: string;
  areaName: string;
  dimensions: ProjectRunAnalysisQuestion[][];
  refetchQuestions: () => void;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  expandAll: boolean;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
  rubricType: string;
  areaRubricCore: Record<string, RubricCore[]> | undefined;
}

const RubricSection: React.FC<RubricSectionProps> = ({
  projectId,
  areaName,
  dimensions,
  refetchQuestions,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  expandAll,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
  rubricType,
  areaRubricCore,
}) => {
  const [dimensionValues, setDimensionValues] = useState<{ [dimension: string]: number }>({});
  const [hasUnprocessedDownstreamChanges, setHasUnprocessedDownstreamChanges] =
    useState<boolean>(false);
  const [expandAccordion, setExpandAccordion] = useState<boolean>(expandAll);

  const toggleAccordion = () => {
    setExpandAccordion(!expandAccordion);
  };

  useEffect(() => {
    setExpandAccordion(expandAll);
  }, [expandAll]);

  const dimensionString = useMemo(
    () => dimensions.map((d) => d[0].dimension).join(', '),
    [dimensions]
  );

  useEffect(() => {
    const dimensionValuesObj = calculateDimensionValues(
      dimensions.map(([question]) => question),
      areaRubricCore
    );
    setDimensionValues(dimensionValuesObj);
  }, [dimensions, areaRubricCore]);

  const overallRating = useMemo(() => calculateOverallRating(dimensionValues), [dimensionValues]);

  return (
    <Accordion
      square
      disableGutters
      sx={{ boxShadow: 'none' }}
      expanded={expandAccordion}
      onChange={toggleAccordion}
    >
      <RubricAccordionSummary expandIcon={<ChevronRightSharp />}>
        <div>
          <Typography sx={{ fontWeight: '700', color: '#092B49' }}>{areaName}</Typography>
          <Typography sx={{ fontWeight: '300', color: '#092B49' }}>{dimensionString}</Typography>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <RubricOverallRating rating={overallRating} rubricType={rubricType} />
          {hasUnprocessedDownstreamChanges && (
            <UnprocessedChangesDot position={{ right: '-20px' }} />
          )}
        </div>
      </RubricAccordionSummary>
      <AccordionDetails sx={{ paddingBottom: 0 }}>
        <TableContainer sx={{ marginLeft: '24px', width: '98%', overflowX: 'visible' }}>
          {dimensions.map((dimensionVersions) => {
            const dimensionName = dimensionVersions[0].dimension as string;
            return (
              <Section key={dimensionName}>
                <RubricRow
                  projectId={projectId}
                  key={dimensionName}
                  dimensionName={dimensionName}
                  textfieldName={`${areaName}${separator}${dimensionName}`}
                  selectName={`${areaName}${separator}${dimensionName}${separator}value`}
                  versions={dimensionVersions}
                  refetchQuestions={refetchQuestions}
                  setIsLoadingThis={setIsLoadingThis}
                  setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                  setDimensionValues={setDimensionValues}
                  setSectionUnprocessedDownstreamChanges={setHasUnprocessedDownstreamChanges}
                  handleFeedbackSubmit={handleFeedbackSubmit}
                  handleValidateFindingsClick={handleValidateFindingsClick}
                  rubricType={rubricType}
                  dimensionRubricCore={areaRubricCore?.[dimensionName]}
                />
              </Section>
            );
          })}
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

export default RubricSection;
